/** Plant detail: KPIs, production chart with irradiance, devices, incidents. */

import { useState } from 'react';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { api } from '../api/client';
import type { Resolution } from '../api/types';
import { FreshnessBadge } from '../components/FreshnessBadge';
import { KpiCard } from '../components/KpiCard';
import { Notice } from '../components/Notice';
import { SeriesView } from '../components/SeriesView';
import { TimeRange, rangeBounds, type RangeKey } from '../components/TimeRange';
import {
  formatCapacity,
  formatDateTime,
  formatEnergy,
  formatNumber,
  formatPercent,
  formatPower,
} from '../lib/format';
import { useApi } from '../lib/useApi';
import { useAuth } from '../lib/auth';

export function PlantPage() {
  const { plantId = '' } = useParams();
  const { t } = useTranslation();
  const { can } = useAuth();
  const [range, setRange] = useState<RangeKey>('today');
  const [resolution, setResolution] = useState<Resolution>('5min');

  const overview = useApi(() => api.plantOverview(plantId), [plantId]);
  const plant0HasSensor = overview.data?.irradiance_available ?? false;
  const devices = useApi(() => api.devices(plantId), [plantId]);
  const incidents = useApi(() => api.incidents({ plantId, openOnly: true }), [plantId]);

  const bounds = rangeBounds(range);
  const power = useApi(
    () => api.plantTelemetry(plantId, { ...bounds, resolution, metrics: 'pac_w' }),
    [plantId, range, resolution],
  );
  // Irradiance is measured by the sensor box, a different device kind — asking
  // the inverters for it would return a column of nulls.
  const irradiance = useApi(
    () =>
      plant0HasSensor
        ? api
            .plantTelemetry(plantId, {
              ...bounds,
              resolution,
              metrics: 'irradiance_wm2',
              kind: 'irradiance_sensor',
            })
            .catch(() => null)
        : Promise.resolve(null),
    [plantId, range, resolution, plant0HasSensor],
  );

  if (overview.errorCode) {
    return <Notice tone="error" code={overview.errorCode} params={overview.errorParams} />;
  }
  if (!overview.data) return <p className="muted">{t('app.loading')}</p>;

  const plant = overview.data;

  return (
    <>
      <h1>
        {plant.plant_name} <span className="muted">· {plant.plant_code}</span>
      </h1>

      <div className="kpi-grid">
        <KpiCard
          label={t('kpi.currentPower')}
          value={formatPower(plant.current_power_w)}
          badge={<FreshnessBadge freshness={plant.power_freshness} />}
          tone={plant.power_freshness.status === 'online' ? 'default' : 'warning'}
        />
        <KpiCard
          label={t('kpi.energyToday')}
          value={formatEnergy(plant.energy_today_wh)}
          hint={
            <>
              {plant.energy_today_method
                ? t(`kpi.energyMethod.${plant.energy_today_method}`, {
                    defaultValue: plant.energy_today_method,
                  })
                : null}
              {plant.energy_today_coverage !== null
                ? ` · ${t('kpi.coverage')} ${formatPercent(plant.energy_today_coverage)}`
                : null}
            </>
          }
        />
        <KpiCard
          label={t('kpi.specificYield')}
          value={
            plant.specific_yield_today_wh_per_wp === null
              ? '—'
              : `${formatNumber(plant.specific_yield_today_wh_per_wp, 2)} Wh/Wp`
          }
          hint={formatCapacity(plant.dc_capacity_wp)}
        />
        <KpiCard
          label={t('kpi.irradiance')}
          value={
            plant.irradiance_available ? `${formatNumber(plant.irradiance_wm2, 0)} W/m²` : '—'
          }
          hint={plant.irradiance_available ? null : t('plant.noIrradiance')}
        />
        <KpiCard
          label={t('kpi.devices')}
          value={`${plant.devices.online}/${plant.devices.total}`}
          hint={
            <>
              {plant.devices.stale > 0 ? `${t('freshness.stale')}: ${plant.devices.stale} · ` : null}
              {plant.devices.offline > 0 ? `${t('freshness.offline')}: ${plant.devices.offline}` : null}
            </>
          }
          tone={plant.devices.offline > 0 ? 'warning' : 'default'}
        />
      </div>

      {plant.notes.includes('power_sum_partial') ? (
        <Notice tone="warning">{t('plant.partialPower')}</Notice>
      ) : null}
      {plant.notes.includes('dc_capacity_unknown') ? (
        <Notice tone="info">{t('plant.capacityUnknown')}</Notice>
      ) : null}

      <section>
        <h2>{t('plant.production')}</h2>
        <TimeRange
          range={range}
          resolution={resolution}
          onRangeChange={setRange}
          onResolutionChange={setResolution}
        />
        {power.errorCode ? (
          <Notice tone="error" code={power.errorCode} params={power.errorParams} />
        ) : power.data ? (
          <SeriesView
            series={[power.data]}
            metric="pac_w"
            irradiance={plant.irradiance_available ? irradiance.data : null}
            title={`${plant.plant_name} — ${t('chart.power')}`}
            axisLabel={t('chart.power')}
          />
        ) : (
          <p className="muted">{t('app.loading')}</p>
        )}
      </section>

      <section>
        <h2>{t('plant.devices')}</h2>
        <div className="table-wrap">
          <table>
            <caption className="sr-only">{t('plant.devices')}</caption>
            <thead>
              <tr>
                <th scope="col">{t('device.name')}</th>
                <th scope="col">{t('device.kind')}</th>
                <th scope="col">{t('device.status')}</th>
                <th scope="col" className="num">{t('device.power')}</th>
                <th scope="col" className="num">{t('device.energyTotal')}</th>
                <th scope="col">{t('device.quality')}</th>
                <th scope="col">{t('device.sampleTime')}</th>
                <th scope="col">{t('device.sourceTime')}</th>
                <th scope="col">{t('device.errorCode')}</th>
                {can('telemetry.export') ? <th scope="col">{t('chart.exportCsv')}</th> : null}
              </tr>
            </thead>
            <tbody>
              {(devices.data ?? []).map((device) => (
                <tr key={device.id}>
                  <th scope="row">
                    {device.name}
                    {device.section_name ? <span className="muted"> · {device.section_name}</span> : null}
                  </th>
                  <td>{t(`device.kinds.${device.kind}`, { defaultValue: device.kind })}</td>
                  <td>
                    <FreshnessBadge
                      freshness={{
                        status: device.status,
                        age_seconds: device.age_seconds,
                        sample_time: device.sample_time,
                        expected_cycle_seconds: plant.power_freshness.expected_cycle_seconds,
                      }}
                      compact
                    />
                  </td>
                  <td className="num">{formatPower(device.pac_w)}</td>
                  <td className="num">{formatEnergy(device.e_total_wh)}</td>
                  <td>
                    {device.quality
                      ? t(`device.qualities.${device.quality}`, { defaultValue: device.quality })
                      : '—'}
                  </td>
                  <td>{formatDateTime(device.sample_time)}</td>
                  <td className="muted">
                    {device.source_time ? formatDateTime(device.source_time) : t('device.noSourceTime')}
                  </td>
                  <td>{device.error_code && device.error_code !== '0' ? device.error_code : '—'}</td>
                  {can('telemetry.export') ? (
                    <td>
                      <a href={api.csvUrl(device.id, { ...bounds, resolution: 'raw' })}>CSV</a>
                    </td>
                  ) : null}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      <section>
        <h2>{t('plant.incidents')}</h2>
        {incidents.data && incidents.data.length > 0 ? (
          <ul className="incident-list">
            {incidents.data.map((incident) => (
              <li key={incident.id} className={`incident incident--${incident.severity}`}>
                <strong>
                  {t(`incidents.rules.${incident.rule_code}`, {
                    defaultValue: incident.rule_code,
                    ...incident.message_params,
                  })}
                </strong>{' '}
                <span className="muted">
                  {t(`incidents.severities.${incident.severity}`)} ·{' '}
                  {formatDateTime(incident.opened_at)}
                </span>
              </li>
            ))}
          </ul>
        ) : (
          <p className="muted">{t('incidents.none')}</p>
        )}
      </section>
    </>
  );
}
